import type { DescMessage, MessageInitShape } from '@bufbuild/protobuf';
import { StatusSchema } from '@grpc-gen/google/rpc/status_pb';
import { Code } from '@grpc-gen/google/rpc/code_pb';

import { decodeBinaryHeader } from './decode-binary-header';

interface OutgoingDetail {
  type?: string;
  desc?: DescMessage;
  value: MessageInitShape<DescMessage>;
}

export class GrpcError extends Error {
  public readonly code: Code;
  public readonly rawMessage: string;
  public readonly metadata: Headers;
  public override readonly cause: unknown;
  public details: OutgoingDetail[];

  constructor(
    message: string,
    code: Code = Code.UNKNOWN,
    metadata?: HeadersInit,
    outgoingDetails?: OutgoingDetail[],
    cause?: unknown,
  ) {
    super(createMessage(message, code));
    Object.setPrototypeOf(this, new.target.prototype);
    this.rawMessage = message;
    this.code = code;
    this.metadata = new Headers(metadata ?? {});
    this.details = outgoingDetails ?? [];
    this.cause = cause;
  }

  public static from(reason: unknown, code = Code.UNKNOWN): GrpcError {
    if (reason instanceof GrpcError) {
      return reason;
    }
    if (reason instanceof Error) {
      if (reason.name == 'AbortError') {
        return new GrpcError(reason.message, Code.CANCELLED);
      }
      return new GrpcError(reason.message, code, undefined, undefined, reason);
    }
    return new GrpcError(String(reason), code, undefined, undefined, reason);
  }
}

function createMessage(message: string, code: Code): string {
  return message.length
    ? `[${codeToString(code)}] ${message}`
    : `[${codeToString(code)}]`;
}

function codeToString(value: Code): string {
  const name = Code[value] as string | undefined;
  if (typeof name != 'string') {
    return value.toString();
  }
  return (
    name[0].toLowerCase() +
    name.substring(1).replace(/[A-Z]/g, (c) => '_' + c.toLowerCase())
  );
}

export function isGrpcError(error: unknown): error is GrpcError {
  return error instanceof GrpcError;
}

export function detectGrpcError(response: Response): GrpcError | undefined {
  // TODO Need to reconsider the approach to handling errors in specific statuses
  if (!response.ok && response.status === 401) {
    return new GrpcError('Unauthorized', Code.UNAUTHENTICATED);
  }

  if (!response.ok && response.status === 403) {
    return new GrpcError('Permission Denied', Code.PERMISSION_DENIED);
  }

  if (response.body === null) {
    return new GrpcError('No response body', Code.UNKNOWN);
  }

  return findTrailerError(response.headers);
}

function findTrailerError(headers: Headers): GrpcError | undefined {
  const statusBytes = headers.get('grpc-status-details-bin');
  if (statusBytes) {
    // TODO: Need to regenerate StatusSchema through the existed Status proto file (create next to the session.proto)
    const status = decodeBinaryHeader(statusBytes, StatusSchema);
    if (status.code === 0) {
      return undefined;
    }

    const error = new GrpcError(status.message, status.code, headers);
    error.details = status.details.map(
      (any: { typeUrl: string; value: any }) => ({
        type: any.typeUrl.substring(any.typeUrl.lastIndexOf('/') + 1),
        // TODO: Need to recheck the approach with creating GrpcError
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        value: any.value,
      }),
    );
    return error;
  }

  const grpcStatus = headers.get('grpc-status');
  if (grpcStatus && grpcStatus !== '0') {
    const code = parseInt(grpcStatus, 10);
    return new GrpcError(
      decodeURIComponent(headers.get('grpc-message') || ''),
      code,
      headers,
    );
  }

  return undefined;
}
